import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ShieldAlert, UserRoundCheck } from "lucide-react";
import { toast } from "sonner";

// Simple age calculator
const calcAge = (isoDate?: string | null) => {
  if (!isoDate) return null;
  const d = new Date(isoDate);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
};

interface ConnectOnboardingProps {
  isPopup?: boolean;
  embed?: boolean;
  modeProp?: "start" | "resume";
}

const ConnectOnboarding = ({ embed: embedProp = false, modeProp, isPopup = false }: ConnectOnboardingProps) => {
  const { user, profile } = useAuth();
  const [params] = useSearchParams();
  const paramMode = (params.get("mode") || "start") as "start" | "resume";
  const mode = (modeProp ?? paramMode) as "start" | "resume"; // "start" | "resume"
  const stripeReturn = params.get("stripe"); // "return" | "refresh"
  const embed = false; // Force popup to avoid iframe restrictions by Stripe

  const [dob, setDob] = useState<string | "">(profile?.["date_of_birth" as any] ?? "");
  const age = useMemo(() => calcAge(dob || null), [dob]);
  const isMinor = age !== null && age < 18;

const [loading, setLoading] = useState(false);
const [status, setStatus] = useState<{charges_enabled?: boolean; payouts_enabled?: boolean; details_submitted?: boolean} | null>(null);
const [onboardingUrl, setOnboardingUrl] = useState<string | null>(null);
const [guardianEmail, setGuardianEmail] = useState<string>("");

// Navigate to Stripe URL - opens in new tab and focuses it
const openStripeWindow = (url: string) => {
  // Use window.open with explicit focus - this ensures the tab opens in foreground
  const win = window.open(url, '_blank');
  if (win) {
    win.focus();
  }
};
  // If Stripe redirects back here, notify opener and close
  useEffect(() => {
    if (stripeReturn) {
      // Refresh status with retry logic for production onboarding
      (async () => {
        let attempts = 0;
        const maxAttempts = 10;
        let statusUpdated = false;
        
        // Poll status until charges/payouts are enabled or max attempts reached
        while (attempts < maxAttempts && !statusUpdated) {
          try {
            await new Promise(resolve => setTimeout(resolve, 2000));
            
            const { data } = await supabase.functions.invoke("connect-account-status");
            
            if (data?.connected) {
              // In test mode, accounts should be immediately enabled
              // In production, they'll be enabled once onboarding is complete
              statusUpdated = data.charges_enabled && data.payouts_enabled;
              
              if (statusUpdated) {
                console.log('Account fully enabled');
                break;
              }
            }
            
            attempts++;
            console.log(`Status check attempt ${attempts}/${maxAttempts}`, data);
          } catch (err) {
            console.error('Error refreshing status:', err);
            attempts++;
          }
        }
        
        // Notify parent window regardless of status
        try {
          if (isPopup) {
            window.parent.postMessage({ type: "stripe-onboarding-finished" }, "*");
            console.log('Sent message to parent window (popup mode)');
          } else if (window.opener) {
            window.opener.postMessage({ type: "stripe-onboarding-finished" }, "*");
            console.log('Sent message to parent window');
          }
        } catch (err) {
          console.error('Error sending message:', err);
        }
        
        // Close window (only if not in popup)
        if (!isPopup) {
          setTimeout(() => {
            window.close();
          }, 1000);
        }
      })();
    }
  }, [stripeReturn, isPopup]);


  const saveDob = async () => {
    if (!user) return;
    if (!dob) return toast.error("Please enter your date of birth");
    setLoading(true);
    try {
      const { error } = await supabase.from("profiles").update({ date_of_birth: dob }).eq("id", user.id);
      if (error) throw error;
      toast.success("Date of birth saved");
    } catch (e: any) {
      toast.error(e.message || "Failed to save date of birth");
    } finally {
      setLoading(false);
    }
  };

const startAdultOnboarding = async () => {
  setLoading(true);
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) throw new Error("Not authenticated");

    if (!dob) throw new Error("Please add your date of birth first.");

    const { data, error } = await supabase.functions.invoke("connect-create-account", {
      headers: {
        Authorization: `Bearer ${sessionData.session.access_token}`,
      },
    });
    if (error) throw error;
    const url = data?.url as string | undefined;
    if (!url) throw new Error("Missing onboarding URL");
    if (embed) {
      setOnboardingUrl(url);
    } else {
      openStripeWindow(url);
    }
  } catch (e: any) {
    const raw = JSON.stringify(e || {});
    if (raw.includes("DOB_REQUIRED") || raw.toLowerCase().includes("date_of_birth")) {
      toast.error("Please add your date of birth first.");
    } else if (raw.toLowerCase().includes("unauthorized") || raw.toLowerCase().includes("authorization")) {
      toast.error("Please sign in and try again.");
    } else {
      const msg = e?.context?.error || e?.error || e?.message || "Could not start onboarding";
      toast.error(msg);
    }
  } finally {
    setLoading(false);
  }
};

const startMinorOnboarding = async () => {
  setLoading(true);
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) throw new Error("Not authenticated");

    if (!guardianEmail || !guardianEmail.includes("@")) {
      throw new Error("Please enter a valid parent/guardian email");
    }
    
    const { data, error } = await supabase.functions.invoke("connect-create-account", {
      headers: {
        Authorization: `Bearer ${sessionData.session.access_token}`,
      },
      body: { guardian_email: guardianEmail },
    });
    if (error) throw error;
    const url = data?.url as string | undefined;
    if (!url) throw new Error("Missing onboarding URL");
    if (embed) {
      setOnboardingUrl(url);
    } else {
      openStripeWindow(url);
    }
  } catch (e: any) {
    const raw = JSON.stringify(e || {});
    if (raw.toLowerCase().includes("unauthorized") || raw.toLowerCase().includes("authorization")) {
      toast.error("Please sign in and try again.");
    } else {
      const msg = e?.context?.error || e?.error || e?.message || "Could not start onboarding for minors";
      toast.error(msg);
    }
  } finally {
    setLoading(false);
  }
};

const resumeOnboarding = async () => {
  setLoading(true);
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    if (!sessionData.session) throw new Error("Not authenticated");
    
    const { data, error } = await supabase.functions.invoke("connect-account-link", {
      headers: {
        Authorization: `Bearer ${sessionData.session.access_token}`,
      },
    });
    if (error) throw error;
    const url = data?.url as string | undefined;
    if (!url) throw new Error("Missing onboarding URL");
    if (embed) {
      setOnboardingUrl(url);
    } else {
      openStripeWindow(url);
    }
  } catch (e: any) {
    const raw = JSON.stringify(e || {});
    if (raw.toLowerCase().includes("unauthorized") || raw.toLowerCase().includes("authorization")) {
      toast.error("Please sign in and try again.");
    } else {
      const msg = e?.context?.error || e?.error || e?.message || "Could not resume onboarding";
      toast.error(msg);
    }
  } finally {
    setLoading(false);
  }
};


  return (
    <div className={embed ? "w-full" : "min-h-screen bg-background text-foreground flex items-center justify-center p-6"}>
      <Card className={embed ? "w-full border-0 shadow-none" : "w-full max-w-xl"}>
        <CardHeader className={embed ? "px-0 pb-4" : undefined}>
          <CardTitle className="font-poppins text-2xl">Payouts setup</CardTitle>
        </CardHeader>
        <CardContent className={embed ? "px-0 space-y-6" : "space-y-6"}>
          {onboardingUrl ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading Stripe Connect…
              </div>
              <div className="rounded-md border overflow-hidden">
                <iframe
                  src={onboardingUrl}
                  className="w-full h-[90vh] bg-background"
                  title="Stripe Connect Onboarding"
                  allow="payment"
                  sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-top-navigation"
                />
              </div>
            </div>
          ) : mode === "resume" ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Reopening Stripe onboarding…
            </div>
          ) : (
            <>
              {/* DOB step - only if missing */}
              {!dob ? (
                <div className="space-y-2">
                  <Label>Date of birth</Label>
                  <div className="flex gap-2">
                    <Input type="date" value={dob || ""} onChange={(e) => setDob(e.target.value)} className="max-w-[220px]" />
                    <Button variant="outline" onClick={saveDob} disabled={loading}>
                      {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>) : "Save"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <Label>Date of birth</Label>
                  <p className="text-sm text-muted-foreground">{dob} {age !== null && `(Age ${age})`}</p>
                </div>
              )}

              {/* Minor guidance */}
              {age !== null && age < 18 ? (
                <div className="space-y-4">
                  <div className="p-3 rounded-md border border-border bg-muted/40 flex items-start gap-3">
                    <ShieldAlert className="h-5 w-5 mt-0.5" />
                    <div>
                      <p className="font-medium">We’re excited to have you on AnySkill!</p>
                      <p className="text-sm text-muted-foreground">
                        If you’re under 18, you can still earn from your skills. Just ask a parent or guardian to link their
                        Stripe Connect account—or create one during setup—so payouts go to them safely.
                      </p>
                    </div>
                  </div>

                  <ol className="list-decimal list-inside text-sm text-muted-foreground space-y-1">
                    <li>Grab a parent or guardian.</li>
                    <li>Click Continue to open Stripe’s secure setup.</li>
                    <li>They’ll connect or create their Stripe account.</li>
                    <li>That’s it—you can start earning from your skills.</li>
                  </ol>

                  <div className="space-y-2">
                    <Label>Parent or guardian email</Label>
                    <Input
                      type="email"
                      placeholder="parent@example.com"
                      value={guardianEmail}
                      onChange={(e) => setGuardianEmail(e.target.value)}
                    />
                    <p className="text-xs text-muted-foreground">We’ll use this email to set up payouts safely.</p>
                  </div>

                  <Button onClick={startMinorOnboarding} disabled={loading} className="w-full">
                    {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Preparing Stripe…</>) : "Continue"}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <UserRoundCheck className="h-4 w-4" />
                    When you continue, we’ll open the secure Stripe Connect onboarding here.
                  </div>
                  <Button onClick={startAdultOnboarding} disabled={loading} className="w-full">
                    {loading ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" />Preparing Stripe…</>) : "Continue"}
                  </Button>
                </div>
              )}

              {/* Optional progress hint */}
              {status && (
                <div className="pt-2 text-xs text-muted-foreground">
                  Status: details {status.details_submitted ? "submitted" : "missing"}, charges {status.charges_enabled ? "enabled" : "disabled"}, payouts {status.payouts_enabled ? "enabled" : "disabled"}.
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ConnectOnboarding;
